import { Component } from '@theme/component';
import { fetchConfig, preloadImage, themeQuantityControlsDisabled } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { requestSkinOnlyConfirmation } from '@theme/skin-only-confirm';
import { showTesoCartAlert } from '@theme/teso-cart-alert';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const ADDED_STATE_DISPLAY_DURATION = 1200;
let productFormInstanceCount = 0;

/**
 * Marks a button enabled/disabled while preserving server-rendered
 * unavailability (e.g. $0 price / sold out). When the server marks a button
 * with `data-unavailable="true"`, it should never be re-enabled from the
 * client — quantity/loading UI transitions must respect the structural state.
 *
 * @param {HTMLButtonElement | null | undefined} button
 * @param {boolean} enabled
 */
const setAddToCartButtonEnabled = (button, enabled) => {
  if (!button) return;
  if (enabled && button.dataset.unavailable === 'true') {
    button.disabled = true;
    return;
  }
  button.disabled = !enabled;
};

/**
 * @param {ProductFormComponent | null | undefined} productForm
 * @returns {boolean}
 */
const needsSkinOnlyConfirmation = (productForm) => {
  return productForm?.dataset.skinOnly === 'true' && productForm.dataset.skinOnlyBypass !== 'true';
};

/**
 * @param {ProductFormComponent} productForm
 * @param {HTMLFormElement} form
 */
const confirmSkinOnlyAndSubmit = (productForm, form) => {
  const productTitle = productForm.dataset.productTitle || '';
  requestSkinOnlyConfirmation({ productTitle }).then((confirmed) => {
    if (!confirmed) {
      productForm.clearSkinOnlyLoadingState();
      return;
    }
    productForm.dataset.skinOnlyBypass = 'true';
    form.requestSubmit();
  });
};

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button, unless the server marked it structurally
   * unavailable (e.g. $0 price / unpriced variant).
   */
  enable() {
    setAddToCartButtonEnabled(this.refs.addToCartButton, true);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (productForm && needsSkinOnlyConfirmation(productForm)) {
      event.preventDefault();
      event.stopPropagation();
      confirmSkinOnlyAndSubmit(productForm, form);
      return;
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    }
  }

  #animateFlyToCart = () => {
    const destination = document.querySelector('[data-testid="cart-icon"]');
    if (!(destination instanceof Element)) return;

    const flyToCart = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCart.source = this.refs.addToCartButton;
    flyToCart.destination = destination;
    flyToCart.useSourceSize = false;
    flyToCart.classList.add(this.closest('.quick-add') ? 'fly-to-cart--quick' : 'fly-to-cart--main');
    document.body.appendChild(flyToCart);
  };

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #updatingMainCartQuantity = false;

  /** @type {number | null} */
  #queuedMainCartQuantity = null;

  /** @type {number | null} */
  #optimisticMainCartQuantity = null;

  /** @type {number} */
  #optimisticMainCartUntil = 0;

  /** @type {boolean} */
  #mainQuantityUiPending = false;

  /** @type {number} */
  #lastResolvedMainCartQuantity = 0;

  /**
   * Temporarily toggles data-added on add-to-cart buttons so
   * existing success-state UI (checkmark burst/text transition) can run.
   * @param {NodeListOf<AddToCartComponent>} addToCartContainers
   */
  #showAddedState(addToCartContainers) {
    for (const container of addToCartContainers) {
      const button = container.refs.addToCartButton;
      if (!button) continue;

      button.removeAttribute('data-added');
      // Force reflow so rapid consecutive adds can replay the transition.
      void button.offsetWidth;
      button.setAttribute('data-added', 'true');

      window.setTimeout(() => {
        if (button.isConnected && button.getAttribute('data-added') === 'true') {
          button.removeAttribute('data-added');
        }
      }, ADDED_STATE_DISPLAY_DURATION);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.id) {
      productFormInstanceCount += 1;
      this.id = `product-form-${this.dataset.productId || 'unknown'}-${productFormInstanceCount}`;
    }

    const { signal } = this.#abortController;
    // Bunnings cards (`.bunnings-product-card`) render inside other sections,
    // e.g. "You might also like" in the main product section. Scope them to the
    // card so the host product's variant events never hide or disable them.
    const target = this.closest('.bunnings-product-card, .shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#syncMainQuantityFromMarkup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    let cartQty = cartItem ? cartItem.quantity : 0;

    // Cart endpoint can briefly return stale 0 just after a successful add.
    // Preserve optimistic quantity for a short window to avoid strip->button flicker.
    if (
      cartQty === 0 &&
      this.#optimisticMainCartQuantity &&
      Date.now() < this.#optimisticMainCartUntil
    ) {
      cartQty = this.#optimisticMainCartQuantity;
    } else if (cartQty > 0) {
      this.#optimisticMainCartQuantity = null;
      this.#optimisticMainCartUntil = 0;
    }

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
    this.#lastResolvedMainCartQuantity = cartQty;
    this.#setMainAddToCartQuantityUi(cartQty);
    this.#setAddToCartLoading(false);

    return cartQty;
  }

  /**
   * Syncs initial main add-to-cart quantity UI from server-rendered markup.
   */
  #syncMainQuantityFromMarkup() {
    if (themeQuantityControlsDisabled()) {
      this.#setMainAddToCartQuantityUi(0);
      return;
    }

    const quantityWrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity]'));
    const quantityValue = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity-value]'));
    if (!quantityWrapper || !quantityValue) return;

    const parsedQty = parseInt(quantityValue.textContent || '0', 10) || 0;
    this.#setMainAddToCartQuantityUi(parsedQty);
  }

  /**
   * Updates main add-to-cart button/stepper UI from cart quantity.
   * @param {number} cartQty - Quantity in cart for the selected variant.
   */
  #setMainAddToCartQuantityUi(cartQty) {
    const addToCartButton = /** @type {HTMLElement | null} */ (this.querySelector('[data-testid="standalone-add-to-cart"]'));
    const addToCartStrip = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-add-to-cart-strip]'));
    const quantityWrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity]'));
    const quantityValue = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity-value]'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));

    const qtyControlsOff = themeQuantityControlsDisabled();
    const requiresVariantChoice = Boolean(
      this.closest('[data-bunnings-needs-variant-choice], [data-kiosk-needs-variant-choice]')
    );
    const defaultQty = parseInt(quantityInput?.min || this.dataset.quantityDefault || '1', 10) || 1;

    if (!addToCartButton) return;

    if (this.#mainQuantityUiPending) {
      addToCartStrip?.setAttribute('hidden', '');
      quantityWrapper?.setAttribute('hidden', '');
      addToCartButton.style.display = '';
      addToCartButton.dataset.loading = 'true';
      addToCartButton.disabled = true;
      return;
    }

    addToCartButton.dataset.loading = 'false';

    if (qtyControlsOff) {
      cartQty = 0;
      addToCartStrip?.removeAttribute('hidden');
      addToCartButton.style.display = '';
      quantityWrapper?.setAttribute('hidden', '');
      setAddToCartButtonEnabled(addToCartButton, true);
      if (quantityInput) quantityInput.value = String(defaultQty);
      return;
    }

    if (requiresVariantChoice) {
      addToCartStrip?.removeAttribute('hidden');
      addToCartButton.style.display = '';
      quantityWrapper?.setAttribute('hidden', '');
      setAddToCartButtonEnabled(addToCartButton, true);
      if (quantityInput) quantityInput.value = String(defaultQty);
      return;
    }

    if (!quantityWrapper || !quantityValue) return;

    const defaultQuantity = defaultQty;
    const hasQty = cartQty > 0;

    if (hasQty) {
      quantityWrapper.removeAttribute('hidden');
      if (addToCartStrip) addToCartStrip.setAttribute('hidden', '');
      addToCartButton.style.display = 'none';
      setAddToCartButtonEnabled(addToCartButton, true);
      quantityValue.textContent = cartQty.toString();
      if (quantityInput) quantityInput.value = cartQty.toString();
    } else {
      quantityWrapper.setAttribute('hidden', '');
      if (addToCartStrip) addToCartStrip.removeAttribute('hidden');
      addToCartButton.style.display = '';
      setAddToCartButtonEnabled(addToCartButton, true);
      quantityValue.textContent = defaultQuantity.toString();
      if (quantityInput) quantityInput.value = defaultQuantity.toString();
    }
  }

  /**
   * Toggles loading UI for PDP add-to-cart buttons.
   * @param {boolean} isLoading
   */
  #setAddToCartLoading(isLoading) {
    const mainAddToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-testid="standalone-add-to-cart"]')
    );
    if (mainAddToCartButton) {
      mainAddToCartButton.dataset.loading = isLoading ? 'true' : 'false';
      setAddToCartButtonEnabled(mainAddToCartButton, !isLoading);
    }

    const stickyAddToCartButton = /** @type {HTMLButtonElement | null} */ (document.querySelector('[data-testid="sticky-add-to-cart-button"]'));
    if (stickyAddToCartButton) {
      stickyAddToCartButton.dataset.loading = isLoading ? 'true' : 'false';
      setAddToCartButtonEnabled(stickyAddToCartButton, !isLoading);
    }
  }

  /**
   * Optimistically updates main add-to-cart quantity and syncs server.
   * @param {number} quantity - Desired quantity in cart for current variant.
   */
  async #updateMainAddToCartQuantity(quantity) {
    if (themeQuantityControlsDisabled()) return;
    const nextQuantity = Math.max(0, quantity);
    this.#queuedMainCartQuantity = nextQuantity;
    this.#setMainAddToCartQuantityUi(nextQuantity);

    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return;

    if (this.#updatingMainCartQuantity) return;

    while (this.#queuedMainCartQuantity != null) {
      const queuedQuantity = this.#queuedMainCartQuantity;
      this.#queuedMainCartQuantity = null;
      this.#updatingMainCartQuantity = true;

      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates: {
              [variantIdInput.value]: queuedQuantity,
            },
          }),
        });

        if (!response.ok) throw new Error(`Failed to update cart quantity: ${response.status}`);

        const cart = /** @type {Cart} */ (await response.json());
        const hasNewerQueuedQuantity = this.#queuedMainCartQuantity != null;
        if (hasNewerQueuedQuantity) {
          // Skip publishing stale intermediate states while newer quantity is queued.
          continue;
        }

        this.#updateCartQuantityFromData(cart);

        this.dispatchEvent(
          new CartUpdateEvent(cart, this.id, {
            source: 'product-form-component',
            productId: this.dataset.productId,
            variantId: variantIdInput.value,
            itemCount: queuedQuantity,
          })
        );
      } catch (error) {
        console.error(error);
        await this.#fetchAndUpdateCartQuantity();
      } finally {
        this.#updatingMainCartQuantity = false;
      }
    }
  }

  /**
   * Increases quantity from the main add-to-cart quantity stepper.
   * @param {Event} event
   */
  increaseMainAddToCartQuantity(event) {
    event.preventDefault();
    if (themeQuantityControlsDisabled()) return;
    const quantityValue = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity-value]'));
    const currentQuantity = parseInt(quantityValue?.textContent || '0', 10) || 0;
    void this.#updateMainAddToCartQuantity(currentQuantity + 1);
  }

  /**
   * Decreases quantity from the main add-to-cart quantity stepper.
   * @param {Event} event
   */
  decreaseMainAddToCartQuantity(event) {
    event.preventDefault();
    if (themeQuantityControlsDisabled()) return;
    const quantityValue = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity-value]'));
    const currentQuantity = parseInt(quantityValue?.textContent || '0', 10) || 0;
    void this.#updateMainAddToCartQuantity(currentQuantity - 1);
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<Cart|null>} The latest cart payload, if available
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      const cart = await response.json();
      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id) return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }

    this.#setAddToCartLoading(false);
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    if (needsSkinOnlyConfirmation(this)) {
      confirmSkinOnlyAndSubmit(this, form);
      return;
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });
    this.#setAddToCartLoading(true);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          // Detect a submission proxied from the sticky add-to-cart bar (a
          // "puppet" click). When it is, the PDP inline error region under the
          // buy buttons is scrolled out of view, so the server error would be
          // invisible. Capture this before dispatching CartErrorEvent, which
          // resets the sticky bar's puppet flag.
          const triggeredFromStickyBar = Array.from(allAddToCartContainers).some(
            (container) => container.refs.addToCartButton?.dataset.puppet === 'true'
          );

          this.#setAddToCartLoading(false);
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          // Surface the server validation error (e.g. the mixed TESO + merch
          // cart block) in the global alert modal when there is no inline error
          // region (card/quick-add surfaces) OR when the inline region is out
          // of view because the add came from the sticky bar.
          if (!addToCartTextError || triggeredFromStickyBar) {
            showTesoCartAlert(response.message);
          }

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
          }

          // Announce for screen readers regardless of which surface is used.
          this.#setLiveRegionText(response.message);

          // Card/quick-add surfaces have no inline error node, so the clear must
          // be scheduled unconditionally or the announcement is never reset.
          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            addToCartTextError?.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          await this.#fetchAndUpdateCartQuantity();

          return;
        } else {
          const variantId = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!variantId) throw new Error('Form ID is required');

          const requestedQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
          const quantityValueEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity-value]'));
          const quantityWrapperEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-main-sticky-quantity]'));
          const displayedQty = parseInt(quantityValueEl?.textContent || '0', 10) || 0;
          // Hidden quantity strip means "not in cart yet", regardless of placeholder/default text.
          const currentQty = quantityWrapperEl?.hasAttribute('hidden') ? 0 : displayedQty;
          const optimisticQty = Math.max(1, currentQty + requestedQuantity);
          this.#optimisticMainCartQuantity = optimisticQty;
          this.#optimisticMainCartUntil = Date.now() + 2000;
          this.#setMainAddToCartQuantityUi(optimisticQty);
          this.#showAddedState(allAddToCartContainers);

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const refreshedCart = await this.#fetchAndUpdateCartQuantity();
          this.#setAddToCartLoading(false);

          this.dispatchEvent(
            new CartAddEvent(refreshedCart || {}, this.id, {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: variantId.toString(),
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        void this.#fetchAndUpdateCartQuantity();
        this.#setAddToCartLoading(false);
      })
      .finally(() => {
        delete this.dataset.skinOnlyBypass;
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Resets loading state when a skin-only confirmation is cancelled.
   */
  clearSkinOnlyLoadingState() {
    this.#setAddToCartLoading(false);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id ?? '');
    } else {
      // Only ignore cross-product updates when both IDs are known and disagree.
      // Strict !== breaks when types differ (e.g. number vs string) or when the picker
      // omits data-product-id; either case left the ATC disabled after variant:selected.
      const dataPid = event.detail.data.productId ?? '';
      const formPid = this.dataset.productId ?? '';
      if (dataPid !== '' && formPid !== '' && String(dataPid) !== String(formPid)) {
        return;
      }
    }

    // Only after the guard: the pending state hides the CTA until the finally
    // block below clears it, so an ignored event must not set it.
    this.#mainQuantityUiPending = true;
    this.#setMainAddToCartQuantityUi(0);

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
      this.#mainQuantityUiPending = false;
      this.#setAddToCartLoading(false);
      return;
    }

    // If we have no resource or no re-rendered button to morph from, fall back to
    // the `variant.available` signal. Otherwise, let the server-rendered markup
    // drive the enabled/disabled state via morph — `blocks/buy-buttons.liquid`
    // owns the full CTA state machine (has_price, quantity rules, inventory
    // policy), and `variant.available` alone is a strict subset that can
    // re-enable a button that should stay disabled (e.g. $0 variant, or when a
    // prior morph cleared the data-unavailable marker).
    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');

    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      // morph() defaults to childrenOnly, so it updates the label but not the
      // button's own attributes. Copy the server's CTA state explicitly (Sold
      // out and Unavailable both render `disabled data-unavailable="true"`);
      // otherwise the initial render's state sticks across variant switches.
      const unavailable = newAddToCartButton.getAttribute('data-unavailable');
      if (unavailable === null) {
        currentAddToCartButton.removeAttribute('data-unavailable');
      } else {
        currentAddToCartButton.setAttribute('data-unavailable', unavailable);
      }
      // Hold the pending lock until the cart quantity refresh settles; the
      // finally block re-enables via setAddToCartButtonEnabled, which respects
      // data-unavailable.
      currentAddToCartButton.disabled = newAddToCartButton.hasAttribute('disabled') || this.#mainQuantityUiPending;
    } else if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    try {
      if (!hasB2BFeatures) {
        await this.#fetchAndUpdateCartQuantity();
        return;
      }

      // Fetch and update cart quantity for the new variant
      await this.#fetchAndUpdateCartQuantity();
    } finally {
      this.#mainQuantityUiPending = false;
      this.#setAddToCartLoading(false);
      this.#setMainAddToCartQuantityUi(this.#lastResolvedMainCartQuantity);
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
